'use client'

import React, { useState } from 'react'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import { useTheme } from '../theme/provider'
import { HeroCarousel, CarouselSlide } from './hero-carousel'
import { ArticleCard, Article } from './article-card'
import { SidebarTabs, TabItem } from './sidebar-tabs'
import { Button } from './button'
import { Play, Clock } from 'lucide-react'

// Dynamic import for StoryViewer - only loads when user opens a story
const StoryViewer = dynamic(
  () => import('./story-viewer').then((mod) => ({ default: mod.StoryViewer })),
  { ssr: false }
)

// Import StoryCollection type separately
import type { StoryCollection } from './story-viewer'

export interface HomePageProps {
  /**
   * Hero carousel slides (featured articles)
   */
  heroSlides: CarouselSlide[]
  /**
   * Latest articles to display in grid
   */
  latestArticles: Article[]
  /**
   * Sidebar tabs content (Most Read, Recent, Popular)
   */
  sidebarTabs: TabItem[]
  /**
   * Story collections to display on left sidebar
   */
  storyCollections?: StoryCollection[]
  /**
   * Function to get article URL
   */
  getArticleUrl: (article: Article) => string
  /**
   * Optional: Function to optimize image URLs
   */
  getOptimizedImageUrl?: (url: string, width?: number, quality?: number) => string
  /**
   * Optional: Function to get category label
   */
  getCategoryLabel?: (article: Article, fallbackIndex?: number) => string
  /**
   * Optional: Function to format date
   */
  formatDate?: (date: string) => string
  /**
   * Optional: Function to strip HTML
   */
  stripHtml?: (html: string) => string
  /**
   * Locale for localization
   */
  locale?: string
  /**
   * Section titles
   */
  sectionTitles?: {
    featured?: string
    latest?: string
    loadMore?: string
    stories?: string
  }
  /**
   * Enable auto-play for hero carousel
   */
  autoPlay?: boolean
  /**
   * Auto-play interval in milliseconds
   */
  autoPlayInterval?: number
  /**
   * On load more click handler
   */
  onLoadMore?: () => void
  /**
   * Loading state for load more
   */
  loadingMore?: boolean
}

/**
 * HomePage Template - Real Madrid Style
 *
 * Full homepage layout with three-column design:
 * - Three-column layout: Left placeholder + Hero carousel (center) + Right placeholder
 * - Latest articles grid (1-4 columns: mobile 1, tablet 2, desktop 3, xl 4)
 * - Responsive design with mobile-first approach
 * - Max width container: 1440px
 *
 * Layout breakdown:
 * - Left Placeholder: 3 columns (desktop only, height: 498px)
 * - Hero Carousel: 6 columns (desktop only, height: 450px)
 * - Right Placeholder: 3 columns (desktop only, height: 498px)
 *
 * NOTE: This component should be wrapped with PageLayout component to ensure
 * consistent header/footer spacing across all pages.
 *
 * @example
 * ```tsx
 * <PageLayout header={headerProps} footer={footerProps}>
 *   <HomePage
 *     heroSlides={featuredArticles}
 *     latestArticles={articles}
 *     sidebarTabs={tabs}
 *     getArticleUrl={(article) => `/news/${article.id}`}
 *     locale="en"
 *   />
 * </PageLayout>
 * ```
 */
export function HomePage({
  heroSlides,
  latestArticles,
  sidebarTabs,
  storyCollections = [],
  getArticleUrl,
  getOptimizedImageUrl,
  getCategoryLabel,
  formatDate,
  stripHtml,
  locale = 'en',
  sectionTitles = {
    featured: 'Featured News',
    latest: 'Latest News',
    loadMore: 'Load More Articles',
    stories: 'Stories',
  },
  autoPlay = true,
  autoPlayInterval = 5000,
  onLoadMore,
  loadingMore = false,
}: HomePageProps) {
  const { theme } = useTheme()
  const [showViewer, setShowViewer] = useState(false)
  const [selectedStoryIndex, setSelectedStoryIndex] = useState(0)

  const openStory = (index: number) => {
    setSelectedStoryIndex(index)
    setShowViewer(true)
  }

  const formatDuration = (itemCount: number) => {
    const seconds = itemCount * 5
    if (seconds < 60) return `${seconds}s`
    const minutes = Math.floor(seconds / 60)
    return `${minutes}m`
  }

  // Filter to show only latest (unviewed) story
  const latestStories = storyCollections.filter((story) => !story.isViewed).slice(0, 1)

  return (
    <div
      className="pb-16 transition-colors duration-300"
      style={{ backgroundColor: theme.colors.background }}
    >
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Left Placeholder + Hero Carousel + Right Placeholder Row */}
        <section className="mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left Placeholder - 3 columns on desktop */}
            <div className="lg:col-span-3 hidden lg:block">
              <div
                className="rounded-2xl border p-6 text-center h-[498px] flex items-center justify-center"
                style={{
                  backgroundColor: theme.colors.surface,
                  borderColor: theme.colors.border,
                  color: theme.colors.textSecondary,
                }}
              >
                <p className="text-sm">Left placeholder content</p>
              </div>
            </div>

            {/* Hero Carousel - 6 columns on desktop (prominent center feature) */}
            <div className="lg:col-span-6">
              <HeroCarousel
                slides={heroSlides}
                autoPlay={autoPlay}
                autoPlayInterval={autoPlayInterval}
                height="450px"
              />
            </div>

            {/* Right Placeholder - 3 columns on desktop */}
            <div className="lg:col-span-3 hidden lg:block">
              <div
                className="rounded-2xl border p-6 text-center h-[498px] flex items-center justify-center"
                style={{
                  backgroundColor: theme.colors.surface,
                  borderColor: theme.colors.border,
                  color: theme.colors.textSecondary,
                }}
              >
                <p className="text-sm">Right placeholder content</p>
              </div>
            </div>
          </div>
        </section>

        {/* Latest Articles Section */}
        <section className="mb-12">
          <div className="flex items-center justify-between mb-8">
            <h2
              className="text-3xl font-bold uppercase tracking-tight"
              style={{ color: theme.colors.text }}
            >
              {sectionTitles.latest}
            </h2>
            <div
              className="h-px flex-1 ml-8"
              style={{
                background: `linear-gradient(to right, ${theme.colors.border}, transparent)`,
              }}
            />
          </div>

          {/* Grid with Articles + Sidebar */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Articles Grid - 9 columns on desktop */}
            <div className="lg:col-span-9">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
                {latestArticles.map((article, index) => (
                  <ArticleCard
                    key={article.id}
                    article={article}
                    index={index}
                    getArticleUrl={getArticleUrl}
                    getOptimizedImageUrl={getOptimizedImageUrl}
                    getCategoryLabel={getCategoryLabel}
                    formatDate={formatDate}
                    stripHtml={stripHtml}
                    loading={index < 6 ? 'eager' : 'lazy'}
                    priority={index < 3}
                    locale={locale}
                  />
                ))}
              </div>

              {/* Load More Button */}
              {onLoadMore && (
                <div className="flex justify-center mt-8">
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={onLoadMore}
                    loading={loadingMore}
                  >
                    {sectionTitles.loadMore}
                  </Button>
                </div>
              )}
            </div>

            {/* Sidebar - 3 columns on desktop */}
            <aside className="lg:col-span-3 hidden lg:block">
              <div className="lg:sticky lg:top-20">
                <SidebarTabs
                  tabs={sidebarTabs}
                  height="650px"
                />
              </div>
            </aside>
          </div>
        </section>
      </div>

      {/* Story Viewer Modal */}
      {showViewer && storyCollections.length > 0 && (
        <StoryViewer
          stories={storyCollections}
          initialStoryIndex={selectedStoryIndex}
          onClose={() => setShowViewer(false)}
        />
      )}
    </div>
  )
}

/**
 * StoryCardVertical Component - Vertical story card for homepage sidebar
 */
interface StoryCardVerticalProps {
  story: StoryCollection
  onClick: () => void
  formatDuration: (itemCount: number) => string
}

function StoryCardVertical({ story, onClick, formatDuration }: StoryCardVerticalProps) {
  const { theme } = useTheme()

  return (
    <a className="block group cursor-pointer" onClick={onClick}>
      <div className="w-full h-[450px] rounded-xl overflow-hidden relative bg-gradient-to-b from-gray-100 to-gray-200 border border-gray-200 shadow-lg">
        {/* Thumbnail Image */}
        <Image
          src={story.thumbnail}
          alt={story.title}
          fill
          className="object-cover"
          loading="eager"
          sizes="300px"
        />

        {/* Overlay Gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/85 to-black/50" />

        {/* VIDÉO Badge - Top Left */}
        <div className="absolute top-3 left-3 z-20">
          <div className="flex items-center gap-1 bg-black/60 backdrop-blur-sm px-2 py-1 rounded-full">
            <Play className="w-3 h-3 text-white fill-white" />
            <span className="text-white text-[9px] font-bold uppercase">Vidéo</span>
          </div>
        </div>

        {/* Large Play Button - Center */}
        <div className="absolute inset-0 flex items-center justify-center z-10">
          <div className="w-16 h-16 bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center shadow-2xl group-hover:scale-110 group-hover:bg-white/40 transition-all duration-300">
            <Play className="w-8 h-8 text-white fill-white ml-1" />
          </div>
        </div>

        {/* Story Info - Bottom */}
        <div className="absolute bottom-3 left-3 right-3">
          <p
            className="text-white text-sm font-bold leading-tight line-clamp-2"
            style={{
              textShadow: 'rgba(0, 0, 0, 0.9) 0px 2px 12px, rgba(0, 0, 0, 0.8) 0px 4px 16px, rgba(0, 0, 0, 0.7) 0px 0px 24px'
            }}
          >
            {story.title}
          </p>
          {story.timestamp && (
            <p
              className="text-white/95 text-xs mt-1 font-semibold"
              style={{
                textShadow: 'rgba(0, 0, 0, 0.9) 0px 2px 8px, rgba(0, 0, 0, 0.7) 0px 4px 12px'
              }}
            >
              {story.timestamp}
            </p>
          )}
        </div>
      </div>
    </a>
  )
}
